import {
    Document,
    Page,
    Text,
    View,
    StyleSheet,
    Font,
    PDFViewer,
    Image,
    usePDF,
    Svg,
    Path,
    Rect
} from "@react-pdf/renderer";
import { useEvent } from "../context/eventContext";
import { getSelectedElement, myTimestampToDate, propComparator, propComparatorDate } from "../utils/ui_utils";
import { differenceInMonths, format, isSameDay } from "date-fns";
import { event_category_list, event_type_list } from "../definitions/event";
import { CowCrossPDF, getCross } from "../definitions/cow";
import { logopath } from "../definitions/anafilogo";

Font.register({
    family: 'Ubuntu',
    fonts: [
        {
            src: 'https://fonts.gstatic.com/s/questrial/v13/QdVUSTchPBm7nuUeVf7EuStkm20oJA.ttf',
        },
        {
            src: 'https://fonts.gstatic.com/s/questrial/v13/QdVUSTchPBm7nuUeVf7EuStkm20oJA.ttf',
            fontWeight: 'bold',
        },
        {
            src: 'https://fonts.gstatic.com/s/questrial/v13/QdVUSTchPBm7nuUeVf7EuStkm20oJA.ttf',
            fontWeight: 'normal',
            fontStyle: 'italic',
        },
    ],
});


// Create styles
const styles = StyleSheet.create({
    page: {
        backgroundColor: "#fff",
        color: "black",
        margin: 0,
        fontSize: 8,
        padding: 10,
        paddingBottom: 30
    },
    section: {
        margin: 10,
        padding: 10,
    },
    subheader: {
        padding: 0,
        paddingBottom: 3,
        marginBottom: 2,
        flexDirection: "row",
        flexWrap: "wrap",
        borderBottom: '1px solid #3D79D1',
        justifyContent: "center",
        fontSize: 7,
        verticalAlign: "center",
    },
    header: {
        padding: 0,
        paddingBottom: 3,
        marginBottom: 2,
        fontSize: 7,
        verticalAlign: "center",
    },
    title: {
        fontSize: '11px',
        backgroundColor: '#3D79D1',
        padding: 2,
        marginBottom: 5,
        color: 'white',
        fontWeight: 'bold',
        textAlign: "center"
    },
    toptitle: {
        fontSize: '10px',
        //        backgroundColor: '#3D79D1',
        borderBottom: '1px solid #3D79D1',
        marginBottom: 5,
        fontWeight: 'bold',
        padding: 2,
        textAlign: "center"
    },
    row_description: {
        color: '#555',
        fontSize: '7px',
    },
    subtitle: {
        borderTop: '1px solid #3D79D1',
        borderBottom: '1px solid #3D79D1',
        fontSize: '14px',
        padding: 2,
        marginBottom: 10,
        fontWeight: 'bold',
    },
    firstpage_section: {
        border: '1px solid #3D79D1',
        borderRadius: '10px',
        fontSize: '14px',
        margin: '30px',
        padding: 20
    },
    firstpage_subtitle: {
        borderTop: '1px solid #3D79D1',
        borderBottom: '1px solid #3D79D1',
        fontSize: '24px',
        textAlign: 'center',
        padding: 2,
        marginBottom: 10,
        fontFamily: "Helvetica-Bold",
    },
    x_style: {
        fontFamily: "Helvetica-Bold",
        fontSize: '7px'
    },
    catalog: {
        fontFamily: "Helvetica-Bold",
        width: '20px',
    },
    judge_row: {
        flexDirection: "row",
        flexWrap: "wrap",
        justifyContent: "center",
        fontWeight: 'bold',
        verticalAlign: "center",
    },
    judge_container: {
        alignItems: "right",
        textAlign: 'center',
        width: '30%',
        marginTop: 20,
    },
    highlight: {
        color: 'black',
        fontFamily: "Helvetica-Bold",
    },
    image: {
        height: 10,
        marginRight: 10
    },
    cow_container: {
        flexDirection: "row",
        flexWrap: "wrap",
        margin: 0,
        padding: 0,
        alignItems: "center", // Aligns text vertically in the center
    },
    cow_container_border: {
        marginBottom: 1,
        marginTop: 1,
    },
    cowname: {
        fontFamily: "Helvetica-Bold"
    },
    subsection: {
        margin: 10,
        padding: 10,
    },
    firstrow: {
        display: 'flex',
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: "center",
        fontSize: '40px',
        fontFamily: "Helvetica-Bold"
    },
    secondrow: {
        display: 'flex',
        flex: 1,
        borderBottom: '2px solid black',
        justifyContent: 'center',
        alignItems: 'center',
        fontSize: '30px'
    },
    thirdrow: {
        display: 'flex',
        flex: 2,
        justifyContent: 'center',
        alignItems: 'center',
        fontSize: '30px'
    },
    toprow: {
        display: 'flex',
        flex: 0.2,
        fontSize: 7,
        justifyContent: 'center',
        alignItems: 'center',
    },
    footer: {
        borderTop: '1px solid #3D79D1',
        textAlign: 'center',
        position: 'absolute',
        fontSize: 7,
        bottom: 10,
        left: 10,
        right: 10,
        paddingTop: 5,
        textAlign: 'center',
        color: 'grey',

    },
    viewer: {
        width: window.innerWidth - 100, //the pdf viewer will take up all of the width and height
        height: window.innerHeight - 200,
    },
});

const getCategorySection = (id) => {
    if (id < 7) {
        return 1
    } else if (id < 8) {
        return 2
    } else if (id < 13) {
        return 3
    } else {
        return 4
    }
}


const CatalogPdf = ({ doc_data, p_type }) => {
    const { selEvent } = useEvent()

    let _element_list = []
    if (p_type === 'catalog') {
        _element_list = doc_data.category_list.map(c => ({ ...c, cow_list: doc_data.cow_list.filter(cow => cow.category && cow.category.id === c.id) }))
        //    const cow_dict = Object.assign({}, ...doc_data.cow_list.map((x) => ({ [x.id]: x })))
    } else {
        _element_list = doc_data.data_list
    }

    return (
        <PDFViewer style={styles.viewer} showToolbar={true}>
            <Document title={`${selEvent.event_name} - CATALOGO`} author={"ANAFIBJ"} creator={"Showtime ANAFIBJ"} produce={"ANAFIBJ"} >
                {p_type === 'catalog' ?
                    <>
                        <FirstPage selEvent={selEvent} />
                        {_element_list.filter(c => c.cow_list && c.cow_list.length > 0).map((c, k) => (
                            <CategoryPage category={c} key={k} selEvent={selEvent} p_type={p_type} />
                        ))}
                        <CowmanPage cowman_list={doc_data.cowman_list} selEvent={selEvent} cow_list={doc_data.cow_list.sort(propComparatorDate('birthdate', true, 'catalog_id'))} />
                        <SummaryPage selEvent={selEvent} category_list={_element_list} cow_list={doc_data.cow_list} />
                    </>
                    : <>
                        <Page size="A5" style={styles.page}>
                            <PageHeader selEvent={selEvent} subtitle={!doc_data.showNotPresent && 'solo soggetti presenti'} title={doc_data.title} />
                            {_element_list.filter(c => c.cow_list && c.cow_list.length > 0).map((c, k) => (
                                <GroupPage group={c} key={k} selEvent={selEvent} p_type={p_type} title={doc_data.title} showNotPresent={doc_data.showNotPresent} mode={doc_data.mode} />
                            ))}
                            <Text style={styles.footer} render={({ pageNumber, totalPages }) => (
                                `${pageNumber} / ${totalPages}`
                            )} fixed />
                        </Page>
                    </>
                }
            </Document>
        </PDFViewer>
    )

}





const PageHeader = ({ selEvent, title, subtitle }) => {
    const ev_start = myTimestampToDate(selEvent.event_start)
    const ev_end = myTimestampToDate(selEvent.event_end)
    let ev_date = format(ev_start, 'dd/MM/yyyy')
    if (!isSameDay(ev_start, ev_end)) {
        ev_date = `${format(ev_start, 'dd')}-${format(ev_end, 'dd /MM/yyyy')}`
    }

    return (

        <View style={styles.header} fixed>
            <View style={styles.subheader}>
                {selEvent.catalog_show_logo ?
                    <Image
                        style={styles.image}
                        src={logopath}
                    />
                    : <></>
                }
                <View style={{ flex: 2, fontFamily: "Helvetica-Bold", textAlign: "center" }}>
                    <Text>
                        {selEvent.event_name}
                    </Text>
                </View>
                <View style={{ flex: 2, textAlign: "center" }}>
                    <Text>
                        {selEvent.location?.name}
                    </Text>
                </View>
                <View style={{ flex: 1, textAlign: "right" }}>
                    <Text>
                        {ev_date}
                    </Text>
                </View>
                {selEvent.catalog_show_customlogo && selEvent.logo ?
                    <Image
                        style={styles.image}
                        src={selEvent.logo}
                    />
                    : <></>
                }
            </View>
            <View style={{ textAlign: "center", fontSize: 12 }}>
                <Text>{title}{subtitle &&<Text style={{fontSize: '8px'}}>&nbsp;({subtitle})</Text>}</Text>
            </View>
        </View>
    )
}


const PresenceIcon = ({ is_present }) => {
    if (is_present === true) {
        return (
            <Svg width="12" height="9">
                <Rect
                    x="1"
                    rx="2"
                    ry="2"
                    width="10"
                    height="9"
                    fill="rgb(78, 200, 0)"
                />
                <Path
                    d="M 2 2 , 4 7, 10 2"
                    stroke="white"
                    strokeWidth={2}
                />
            </Svg>
        )

    } else {
        return (
            <Svg width="12" height="9">
                <Rect
                    x="1"
                    rx="2"
                    ry="2"
                    width="10"
                    height="9"
                    fill="rgb(200, 20, 0)"
                />
                <Path
                    d="M 2 2 , 9 8"
                    stroke="white"
                    strokeWidth={2}
                />
                <Path
                    d="M 2 9 , 10 2"
                    stroke="white"
                    strokeWidth={2}
                />
            </Svg>
        )
    }
}

const PADDING_CATALOG = 17
const PADDING_GROUP = 29
const SMALLSIZE = '7px'
const OneCow = ({ cow, c_section, index, ev_date, showPresent }) => {


    let property_string = cow.property?.name || ''
    if (cow.coproperty && cow.coproperty.length) {
        property_string = property_string + " | " + cow.coproperty
    }
    property_string = property_string.replace(/ +(?= )/g, '');
    let catalog_id = cow.catalog_id || ''
    //    console.log("ONE COW PDF", cow)
    const birthdate = myTimestampToDate(cow.birthdate)
    const cow_age = differenceInMonths(ev_date, birthdate)
    if (c_section === 1) {
        return (
            <View style={[styles.cow_container_border, { backgroundColor: index % 2 === 0 ? '#eee' : 'white' }]} wrap={false}>
                <View style={{ flexDirection: 'row' }}>
                    <View style={{ width: showPresent ? PADDING_GROUP : PADDING_CATALOG }}>
                        <Text>
                            {catalog_id}
                        </Text>
                    </View>
                    <Text>
                        <Text style={styles.cowname}>{cow.name?.trim()}</Text> {cow.serial}
                        &nbsp;{format(birthdate, 'dd/MM/yyyy')}
                        &nbsp;({cow_age} mesi)
                        <Text style={styles.row_description}>&nbsp;pft </Text> {cow.pft || ''}
                        <Text style={styles.row_description}>&nbsp;ies</Text> {cow.ies || ''}
                        <Text style={styles.row_description}>&nbsp;ics-pr </Text> {cow.ics_pr || ''}
                    </Text>
                    {showPresent &&
                        <View style={{ width: '100%', alignItems: "flex-end" }}>
                            <PresenceIcon is_present={cow.is_present} />
                        </View>
                    }
                </View>
                <Text style={{ marginLeft: 17 }}>
                    <CowCrossPDF cow={cow} x_style={styles.x_style} />
                </Text>
                <Text style={{ marginLeft: 17 }}>
                    {console.log("COW DATA", cow)}
                    <Text style={styles.row_description}>Dati madre: </Text>
                    <Text style={styles.row_description}>&nbsp;età </Text> {cow.mad_age || ''}
                    <Text style={styles.row_description}>&nbsp;gg lat</Text> {cow.mad_gg || ''}
                    <Text style={styles.row_description}>&nbsp;Kg lat </Text> {cow.mad_kg_latte || ''}
                    <Text style={styles.row_description}>&nbsp;% gr</Text> {cow.mad_pergr || ''}
                    <Text style={styles.row_description}>&nbsp;Kg gr </Text> {cow.mad_kggr || ''}
                    <Text style={styles.row_description}>&nbsp;% pt</Text> {cow.mad_perpt || ''}
                    <Text style={styles.row_description}>&nbsp;Kg pt </Text> {cow.mad_kgpt || ''}
                </Text>
                <Text style={{ marginLeft: 17, fontSize: SMALLSIZE }}>
                    {property_string}
                </Text>
            </View>
        )
    } else if (c_section === 3) {
        return (
            <View style={[styles.cow_container_border, { backgroundColor: index % 2 === 0 ? '#eee' : 'white' }]} wrap={false}>
                <View style={{ flexDirection: 'row' }}>
                    <View style={{ width: showPresent ? PADDING_GROUP : PADDING_CATALOG }}>
                        <Text>
                            {catalog_id}
                        </Text>
                    </View>
                    <Text>
                        <Text style={styles.cowname}>{cow.name?.trim()}</Text> {cow.serial}
                        &nbsp;{format(myTimestampToDate(cow.birthdate), 'dd/MM/yyyy')}
                        {parseInt(cow.birth) > 0 && <><Text style={styles.row_description}>&nbsp;parti:</Text> {cow.birth}</>}
                        <Text style={styles.row_description}>&nbsp;pft </Text> {cow.pft || ''}
                        <Text style={styles.row_description}>&nbsp;ies</Text> {cow.ies || ''}
                        <Text style={styles.row_description}>&nbsp;ics-pr </Text> {cow.ics_pr || ''}
                    </Text>
                    {showPresent &&
                        <View style={{ width: '100%', alignItems: "flex-end" }}>
                            <PresenceIcon is_present={cow.is_present} />
                        </View>
                    }
                </View>
                <Text style={{ marginLeft: 17 }}>
                    <CowCrossPDF cow={cow} x_style={styles.x_style} />
                </Text>
                <Text style={{ marginLeft: 17 }}>
                    <Text style={styles.row_description}>gg lat</Text> {cow.gg_milk || ''}
                    <Text style={styles.row_description}>&nbsp;Kg lat </Text> {cow.kg_milk || ''}
                    <Text style={styles.row_description}>&nbsp;% gr</Text> {cow.fatperc || ''}
                    <Text style={styles.row_description}>&nbsp;Kg gr </Text> {cow.fat_kg || ''}
                    <Text style={styles.row_description}>&nbsp;% pt</Text> {cow.proteinperc || ''}
                    <Text style={styles.row_description}>&nbsp;Kg pt </Text> {cow.protein_kg || ''}
                </Text>
                <Text style={{ marginLeft: 17, fontSize: SMALLSIZE }}>
                    {property_string}
                </Text>
            </View>
        )

    } else if (c_section === 4) {
        return (
            <View style={[styles.cow_container_border, { backgroundColor: index % 2 === 0 ? '#eee' : 'white' }]} wrap={false}>
                <View style={{ flexDirection: 'row' }}>
                    <View style={{ width: showPresent ? PADDING_GROUP : PADDING_CATALOG }}>
                        <Text>
                            {catalog_id}
                        </Text>
                    </View>
                    <Text>
                        <Text style={styles.cowname}>{cow.name?.trim()}</Text> {cow.serial}
                        &nbsp;{format(myTimestampToDate(cow.birthdate), 'dd/MM/yyyy')}
                        {parseInt(cow.birth) > 0 && <><Text style={styles.row_description}>&nbsp;parti:</Text> {cow.birth}</>}
                        <Text style={styles.row_description}>&nbsp;pft </Text> {cow.pft || ''}
                        <Text style={styles.row_description}>&nbsp;ies</Text> {cow.ies || ''}
                        <Text style={styles.row_description}>&nbsp;ics-pr </Text> {cow.ics_pr || ''}
                    </Text>
                    {showPresent &&
                        <View style={{ width: '100%', alignItems: "flex-end" }}>
                            <PresenceIcon is_present={cow.is_present} />
                        </View>
                    }
                </View>
                <Text style={{ marginLeft: 17 }}>
                    <CowCrossPDF cow={cow} x_style={styles.x_style} />
                </Text>
                <Text style={{ marginLeft: 17 }}>
                    <Text style={styles.row_description}>gg lat</Text> {cow.gg_milk || ''}
                    <Text style={styles.row_description}>&nbsp;Kg lat </Text> {cow.kg_milk || ''}
                    <Text style={styles.row_description}>&nbsp;% gr</Text> {cow.fatperc || ''}
                    <Text style={styles.row_description}>&nbsp;Kg gr </Text> {cow.fat_kg || ''}
                    <Text style={styles.row_description}>&nbsp;% pt</Text> {cow.proteinperc || ''}
                    <Text style={styles.row_description}>&nbsp;Kg pt </Text> {cow.protein_kg || ''}
                </Text>
                <Text style={{ marginLeft: 17 }}>
                    <Text style={styles.row_description}>lf ord parto</Text> {cow.lf_order || ''}
                    <Text style={styles.row_description}>&nbsp;lf gg lat </Text> {cow.lf_gg || ''}
                    <Text style={styles.row_description}>&nbsp;lf Kg lat </Text> {cow.lf_kg_milk || ''}
                    <Text style={styles.row_description}>&nbsp;% gr</Text> {cow.lfperc_fat || ''}
                    <Text style={styles.row_description}>&nbsp;Kg gr </Text> {cow.lf_kggr || ''}
                    <Text style={styles.row_description}>&nbsp;% pt</Text> {cow.lf_percpt || ''}
                    <Text style={styles.row_description}>&nbsp;Kg pt </Text> {cow.lf_kgpt || ''}
                </Text>
                <Text style={{ marginLeft: 17, fontSize: SMALLSIZE }}>
                    {property_string}
                </Text>
            </View>
        )
    } else if (c_section === 'cowman') {
        return (
            <View style={[styles.cow_container, styles.cow_container_border, { backgroundColor: index % 2 === 0 ? '#eee' : 'white' }]} wrap={false}>
                <View style={{ width: showPresent ? PADDING_GROUP : PADDING_CATALOG }}>
                    <Text>
                        {catalog_id}
                    </Text>
                </View>
                <View style={{ flexDirection: "row", justifyContent: "space-between", flex: 1 }}>
                    <View flex={8}>
                        <Text>
                            <Text style={styles.cowname}>{cow.name?.trim()}</Text> {cow.serial}
                        </Text>
                    </View>
                    <View flex={1}>
                        <Text style={{ textAlign: 'left' }}>
                            Cat.{cow.category?.category_number}
                        </Text>
                    </View>
                </View>
                {showPresent &&
                    <View style={{ alignItems: "flex-end" }}>
                        <PresenceIcon is_present={cow.is_present} />
                    </View>
                }
            </View>
        )


    } else {
        return (
            <View style={[styles.cow_container_border, { backgroundColor: index % 2 === 0 ? '#eee' : 'white' }]} wrap={false}>
                <View style={{ flexDirection: 'row' }}>
                    <View style={{ width: showPresent ? PADDING_GROUP : PADDING_CATALOG }}>
                        <Text>
                            {catalog_id}
                        </Text>
                    </View>
                    <Text>
                        <Text style={styles.cowname}>{cow.name?.trim()}</Text> {cow.serial}
                        &nbsp;{format(myTimestampToDate(cow.birthdate), 'dd/MM/yyyy')}
                        {parseInt(cow.birth) > 0 && <><Text style={styles.row_description}>&nbsp;parti:</Text> {cow.birth}</>}
                        <Text style={styles.row_description}>&nbsp;pft </Text> {cow.pft || ''}
                        <Text style={styles.row_description}>&nbsp;ies</Text> {cow.ies || ''}
                        <Text style={styles.row_description}>&nbsp;ics-pr </Text> {cow.ics_pr || ''}
                    </Text>
                    {showPresent &&
                        <View style={{ width: '100%', alignItems: "flex-end" }}>
                            <PresenceIcon is_present={cow.is_present} />
                        </View>
                    }
                </View>
                <Text style={{ marginLeft: 17 }}>
                    <CowCrossPDF cow={cow} x_style={styles.x_style} />
                </Text>
                <Text style={{ marginLeft: 17 }}>
                    {console.log("COW DATA", cow)}
                    <Text style={styles.row_description}>Dati madre: </Text>
                    <Text style={styles.row_description}>&nbsp;Età </Text> {cow.mad_age || ''}
                    <Text style={styles.row_description}>&nbsp;gg lat</Text> {cow.mad_gg || ''}
                    <Text style={styles.row_description}>&nbsp;Kg lat </Text> {cow.mad_kg_latte || ''}
                    <Text style={styles.row_description}>&nbsp;% gr</Text> {cow.mad_pergr || ''}
                    <Text style={styles.row_description}>&nbsp;Kg gr </Text> {cow.mad_kggr || ''}
                    <Text style={styles.row_description}>&nbsp;% pt</Text> {cow.mad_perpt || ''}
                    <Text style={styles.row_description}>&nbsp;Kg pt </Text> {cow.mad_kgpt || ''}
                </Text>
                <Text style={{ marginLeft: 17, fontSize: SMALLSIZE }}>
                    {property_string}
                </Text>
            </View>
        )

    }

}

const GroupSummaryData = ({ element_list, showNotPresent }) => {
    const total_count = element_list.length
    const present_count = element_list.filter(c => c.is_present === true).length
    const notpresent_count = element_list.filter(c => !c.is_present).length
    const young = showNotPresent ?
        element_list.filter(c => c.category?.category_number < 7).length
        :
        element_list.filter(c => c.category?.category_number < 7 && c.is_present === true).length

    const old = showNotPresent ?
        element_list.filter(c => c.category?.category_number >= 7).length
        :
        element_list.filter(c => c.category?.category_number >= 7 && c.is_present === true).length

    const extra = showNotPresent ? 
        element_list.filter(c => !c.category || !c.category.id).length
        :
        element_list.filter(c => (!c.category || !c.category.id)  && c.is_present === true).length

    return (
        <View style={[styles.subheader, { marginTop: 0 }]}>
            <View style={{ flex: 2 }}>
                <Text style={{ textAlign: "center" }}>
                    Soggetti iscritti:&nbsp;
                    {total_count}
                </Text>
            </View>
            <View style={{ flex: 2 }}>
                <Text style={{ textAlign: "center" }}>
                    {present_count > 0 &&
                        <Text>Presenti: {present_count}&nbsp;</Text>
                    }
                    {notpresent_count > 0 &&
                        <Text>Non presenti: {notpresent_count}&nbsp;</Text>
                    }
                </Text>
            </View>
            <View style={{ flex: 2 }}>
                <Text style={{ textAlign: "center" }}>
                    {young > 0 &&
                        <Text>Manze/Giovenche: {young}&nbsp;</Text>
                    }
                    {old > 0 &&
                        <Text>Vacche: {old}&nbsp;</Text>
                    }
                </Text>
            </View>


        </View>
    )


}
const GroupPage = ({ selEvent, group, title, showNotPresent, mode }) => {

    const ev_date = myTimestampToDate(selEvent.event_start)
    return (
        <View wrap={mode === 'complete'}>
            <View style={[styles.title, { marginBottom: 2 }]}>
                <View>
                    <Text>
                        {group.cdaua_cowman || group.cdaua}
                    </Text>
                </View>
                <View>
                    <Text>
                        {group.name} 
                    </Text>
                </View>
            </View>
            <GroupSummaryData element_list={group.cow_list} showNotPresent={showNotPresent} />
            {mode === 'complete' && group.cow_list.filter(c => c.is_present || showNotPresent).sort(propComparatorDate('birthdate', true, 'catalog_id')).map((c, k) => (
                <OneCow key={k} cow={c} index={k} ev_date={ev_date} c_section={'cowman'
                    //getCategorySection(c.category?.category_number)
                } showPresent />
            ))}
            <View style={{ flexDirection: 'row', marginBottom: 10 }} >
                <Text>


                    {mode === 'catalog' && group.cow_list.filter(c => c.is_present || showNotPresent).sort(propComparatorDate('birthdate', true, 'catalog_id')).map((c, k) => (
                        <Text key={k}>{c.catalog_id} </Text>
                    ))}
                </Text>
            </View>
        </View>
    )
}

const CategoryPage = ({ selEvent, category, cow_list }) => {

    const ev_date = myTimestampToDate(selEvent.event_start)
    const c_section = parseInt(category.category_section)
    return (

        <Page size="A5" style={styles.page}>
            <PageHeader selEvent={selEvent} />
            <View style={styles.toptitle} ><Text>sezione {category.category_section}</Text></View>
            <View style={styles.title}><Text>{category.category_name} - {category.category_description}</Text></View>
            {category.cow_list.sort(propComparatorDate('birthdate', true, 'catalog_id')).map((c, k) => (
                <OneCow key={k} cow={c} index={k} c_section={c_section} ev_date={ev_date} />
            ))}
            {/*             <Text style={styles.footer} render={({ pageNumber, totalPages }) => (
                `${pageNumber} / ${totalPages}`
            )} fixed /> */}
        </Page>
    )
}
const CowmanPage = ({ selEvent, cowman_list: property_list, cow_list }) => {
    const ev_date = myTimestampToDate(selEvent.event_start)
    return (

        <Page size="A5" style={styles.page}>
            <PageHeader selEvent={selEvent} />
            <View style={styles.title} ><Text>Elenco Allevatori</Text></View>
            {property_list.filter(c => cow_list.filter(v => v.property?.id === c.id).length > 0).map((c, k) => (
                <OneCowman key={k} cowman={c} cow_list={cow_list.filter(v => v.property?.id === c.id)} ev_date={ev_date} />
            ))}
        </Page>
    )
}




const FirstPage = ({ selEvent }) => {

    const ev_start = myTimestampToDate(selEvent.event_start)
    const ev_end = myTimestampToDate(selEvent.event_end)
    const today = format(new Date(), 'dd/MM/yyyy')
    let ev_date = format(ev_start, 'dd/MM/yyyy')
    if (!isSameDay(ev_start, ev_end)) {
        ev_date = `${format(ev_start, 'dd')}-${format(ev_end, 'dd /MM/yyyy')}`
    }
    return (
        <Page size="A5" style={styles.page} title={`${selEvent.event_name} - CATALOGO`} author={"ANAFIBJ"} creator={"Showtime ANAFIBJ"} >
            <View style={styles.title}><Text>Scheda di identificazione</Text></View>
            <View style={styles.firstpage_subtitle}><Text>{selEvent.event_name}</Text></View>
            <View style={styles.firstpage_section}>
                <Text>
                    Località:&nbsp;
                    <Text style={styles.cowname}>
                        {selEvent.location?.name}
                    </Text>
                </Text>
                <Text>
                    Tipo:&nbsp;
                    <Text style={styles.cowname}>
                        {getSelectedElement(selEvent.event_category, event_category_list, 'id')?.label}
                    </Text>
                </Text>
                <Text>
                    Data:&nbsp;
                    <Text style={styles.cowname}>
                        {ev_date}
                    </Text>
                </Text>
            </View>
            <View style={styles.firstpage_section}>
                <Text style={[styles.subtitle, { textAlign: 'center' }]}>
                    Limiti di partecipazione
                </Text>
                <Text style={styles.cowname}>
                    {getSelectedElement(selEvent.event_type, event_type_list, 'id').label}
                </Text>
                <Text>
                    id protocollo:&nbsp;
                    <Text style={styles.cowname}>
                        {selEvent.id_protocol}
                    </Text>
                </Text>
                <Text>
                    {selEvent.note || ''}
                </Text>
            </View>
            <Text style={styles.footer} fixed>
                Catalogo elaborato dall'ufficio centrale del libro genealogico ANAFIBJ in data {today}
            </Text>
            {/*             <Text style={styles.footer} render={({ pageNumber, totalPages }) => (
                `${pageNumber} / ${totalPages}`
            )} fixed /> */}
        </Page>

    )

}
const SummaryPage = ({ selEvent, category_list, cow_list }) => {

    const ev_start = myTimestampToDate(selEvent.event_start)
    const ev_end = myTimestampToDate(selEvent.event_end)
    let ev_date = format(ev_start, 'dd/MM/yyyy')
    if (!isSameDay(ev_start, ev_end)) {
        ev_date = `${format(ev_start, 'dd')}-${format(ev_end, 'dd /MM/yyyy')}`
    }
    return (
        <Page size="A5" style={styles.page}>
            <PageHeader selEvent={selEvent} />
            <View style={styles.toptitle} ><Text>Indice</Text></View>
            <View style={styles.title}><Text>Soggetti presentati a concorso</Text></View>
            {category_list.filter(c => c.cow_list.length > 0).map((c, k) => (
                <View style={[styles.cow_container, { backgroundColor: k % 2 === 0 ? '#eee' : 'white' }]} wrap={false}>
                    <View style={{ flex: 5 }}>
                        <Text style={styles.highlight}>
                            {c.category_description}
                        </Text>
                    </View>
                    <View style={{ flex: 1 }}>
                        <Text>
                            dal {c.cow_list[0].catalog_id || ''}
                        </Text>
                    </View>
                    <View style={{ flex: 1 }}>
                        <Text>
                            al {c.cow_list[c.cow_list.length - 1].catalog_id || ''}
                        </Text>
                    </View>
                </View>
            ))}
            {/*             <Text style={styles.footer} render={({ pageNumber, totalPages }) => (
                `${pageNumber} / ${totalPages}`
            )} fixed /> */}
        </Page>

    )

}

const PlaceSection = ({ data }) => {

    return (<>
        {data.comune ? <Text>&nbsp;{data.comune} -</Text> : <></>}
        {data.provincia ? <Text>&nbsp;{data.provincia} -</Text> : <></>}
        {data.regione ? <Text>&nbsp;{data.regione}</Text> : <></>}
    </>)

}


const OneCowman = ({ cowman, cow_list, ev_date }) => {
    return (
        <View style={styles.cow_container_border} wrap={false}>
            <View flexDirection="row" style={{ marginTop: '5px', borderBottom: '1px dotted #3D79D1' }}>
                <Text>
                    {console.log("COWMAN", cowman)}
                    <Text style={{ width: '40px' }}>{cowman.cdaua}&nbsp;</Text>
                    <Text style={{...styles.cowname,  marginLeft: '4px',  }}>{cowman.name}</Text>
                    <Text>
                        <PlaceSection data={cowman} />
                    </Text>
                </Text>
            </View>
            <View style={{ paddingLeft: '5px' }}>
                {cow_list.map((c, k) => (
                    <OneCow c_section='cowman' cow={c} key={k} ev_date={ev_date} index={k} />
                ))}
            </View>
        </View>
    )
}

export default CatalogPdf;